import * as React from "react";
import { Alert, Button } from "antd";
import { useCpFormContext } from "../../../../components/form/useCpFormContext";
import { formSetValue } from "../../../gvc/testNewForm/utils";
import { useResourceValidations, ResourceValidationResult, ResourceSuggestion } from "./useResourceValidations";

interface ResourceValidationAlertProps {
  containerIndex: number;
}

export const ResourceValidationAlert: React.FC<ResourceValidationAlertProps> = ({ containerIndex }) => {
  const { id } = useCpFormContext();
  const { validations } = useResourceValidations(containerIndex);

  const applySuggestion = React.useCallback(
    (suggestion: ResourceSuggestion) => {
      const fieldMap: Record<ResourceSuggestion["field"], { amountPath: string; unitPath: string }> = {
        minCpu: {
          amountPath: `containers.${containerIndex}.minCpu.amount`,
          unitPath: `containers.${containerIndex}.minCpu.unit`,
        },
        cpu: {
          amountPath: `containers.${containerIndex}.cpu.amount`,
          unitPath: `containers.${containerIndex}.cpu.unit`,
        },
        minMemory: {
          amountPath: `containers.${containerIndex}.minMemory.amount`,
          unitPath: `containers.${containerIndex}.minMemory.unit`,
        },
        memory: {
          amountPath: `containers.${containerIndex}.memory.amount`,
          unitPath: `containers.${containerIndex}.memory.unit`,
        },
      };

      const paths = fieldMap[suggestion.field];
      formSetValue(id, paths.amountPath, String(suggestion.value));
      formSetValue(id, paths.unitPath, suggestion.unit);
    },
    [id, containerIndex],
  );

  // Filter validations: if there are GPU errors, only show those
  // (other errors like ratio will likely be resolved by fixing GPU requirements)
  const filteredValidations = React.useMemo(() => {
    const gpuValidations = validations.filter((v) => v.type.startsWith("gpu-"));
    if (gpuValidations.length > 0) {
      return gpuValidations;
    }
    return validations;
  }, [validations]);

  if (filteredValidations.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3 mt-4">
      {filteredValidations.map((validation, index) => (
        <ValidationAlertItem
          key={`${validation.type}-${index}`}
          validation={validation}
          onApplySuggestion={applySuggestion}
        />
      ))}
    </div>
  );
};

interface ValidationAlertItemProps {
  validation: ResourceValidationResult;
  onApplySuggestion: (suggestion: ResourceSuggestion) => void;
}

const ValidationAlertItem: React.FC<ValidationAlertItemProps> = ({ validation, onApplySuggestion }) => {
  return (
    <Alert
      type="error"
      className="w-fit"
      showIcon
      message={validation.message}
      description={
        validation.suggestions.length > 0 ? (
          <div className="flex flex-wrap gap-2 mt-2">
            {validation.suggestions.map((suggestion, index) => (
              <Button key={index} type="primary" size="small" onClick={() => onApplySuggestion(suggestion)}>
                {suggestion.label}
              </Button>
            ))}
          </div>
        ) : undefined
      }
    />
  );
};
